import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import moment from "moment";

import { MEMBER, PAGE } from "./withdrawalModels";
import { PERMANENT_DEACTIVATION } from "../dashboard/clients/client/members/member/coverage/enums/memberCoverageInactivityReasons";

import * as intlActions from "../intl/intlActions";
import * as withdrawalActions from "./withdrawalActions";

import { MessageBox, PublicPage } from "../common";
import WithdrawalForm from "./WithdrawalForm";

class WithdrawalPage extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.withdraw = this.withdraw.bind(this);
  }

  componentWillMount() {
    const {
      clientDesignator,
      billingPeriodKey,
      intlActions,
      withdrawalActions,
      languageCode,
    } = this.props;

    if (!document.getElementById("reCaptchaV2")) {
      const script = document.createElement("script");
      script.id = "reCaptchaV2";
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${languageCode}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        intlActions.setLocale(languageCode);
        setTimeout(
          () =>
            withdrawalActions.request(
              PAGE,
              {
                designator: clientDesignator.toUpperCase(),
                slug: billingPeriodKey,
              },
              { "Accept-Language": languageCode }
            ),
          1000
        );
      };
      document.body.appendChild(script);
    }
  }

  withdraw(values) {
    const {
      billingPeriod: { billingPeriodId },
      client: { clientId, mustIncludeWithdrawalCode, hasWithdrawalRefund },
      coverageGroups,
      languageCode,
      memberGuarantees,
      withdrawalActions,
    } = this.props;
    const {
      gRecaptchaResponse,
      isInactiveReason,
      memberEmail,
      memberIdentifier,
      withdrawalCoveragesSelection,
      verificationCode,
      refundMethod,
      bankTransit,
      bankInstitution,
      bankAccount,
      bankCheckDigit,
      bankAccountOwnerName,
    } = values;
    const permanent = isInactiveReason === PERMANENT_DEACTIVATION.value;

    const flatten = (arr) =>
      arr.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []);
    const guaranteeIds = permanent
      ? memberGuarantees
      : flatten(
          withdrawalCoveragesSelection.map((selection) => {
            return coverageGroups.map((group) => {
              if (group.withdrawalId == selection) {
                return group.clientCoverages.map((guarantee) => {
                  if (memberGuarantees.includes(guarantee.guaranteeId)) {
                    return guarantee.guaranteeId;
                  }
                });
              }
            });
          })
        ).filter((e) => !!e);
    const withdrawalIds = permanent
      ? coverageGroups.map((group) => group.withdrawalId)
      : withdrawalCoveragesSelection;

    let body = {
      billingPeriodId,
      clientId: clientId,
      gRecaptchaResponse,
      guaranteeIds,
      isInactiveReason,
      destinationEmail: memberEmail,
      memberIdentifier,
      withdrawalIds,
    };

    if (mustIncludeWithdrawalCode) {
      body = { ...body, verificationCode };
    }

    if (hasWithdrawalRefund) {
      body = {
        ...body,
        refundMethod,
        bankTransit,
        bankInstitution,
        bankAccount,
        bankCheckDigit,
        bankAccountOwnerName,
      };
    }

    return withdrawalActions.withdraw(MEMBER, body, {
      "Accept-Language": languageCode,
    });
  }

  getWithdrawalFormMessage() {
    const message = this.props.client.localizations[this.props.languageCode][
      "withdrawalFormMessage"
    ];
    if (!!message) {
      return (
        <MessageBox className="alert-info bg-transparent">
          <span>{message}</span>
        </MessageBox>
      );
    }
  }

  render() {
    const {
      billingPeriod,
      billingPeriodKey,
      client,
      clientDesignator,
      coverageGroups,
      languageCode,
      loaded,
      localizations,
      pageExists,
      hasWithdrawalFormMessage,
      hasWithdrawalPopupMessage,
      hasWithdrawalRedirectButton,
    } = this.props;
    if (!loaded) {
      return null;
    }

    let otherLang = "desistement";
    let linkLabel = "FR";
    switch (languageCode) {
      case "fr-CA": {
        otherLang = "withdrawal";
        linkLabel = "EN";
      }
    }
    const languageSwitch = (
      <a href={`/${otherLang}/${clientDesignator}/${billingPeriodKey}`}>
        {linkLabel}
      </a>
    );

    const { startWithdrawalDate, endWithdrawalDate } = billingPeriod;
    const now = moment().startOf("day");

    return (
      <PublicPage
        cssClass="withdrawal-page"
        clientDesignator={clientDesignator}
        languageSwitch={languageSwitch}
        pageExists={pageExists}
      >
        <div>
          <h2>
            <FormattedMessage id="withdrawal.body.title" />
          </h2>
          {hasWithdrawalRedirectButton && (
            <div className="section">
              <p className="pre-wrap">
                {localizations.withdrawalRedirectButtonTitle}
              </p>
              <a
                href={localizations.withdrawalRedirectUrl}
                className="btn btn-lg btn-primary"
                target="_blank"
              >
                {localizations.withdrawalRedirectButtonValue}
              </a>
              <hr />
            </div>
          )}
          <FormattedHTMLMessage id="withdrawal.body.introduction">
            {(m) => <div dangerouslySetInnerHTML={{ __html: m }} />}
          </FormattedHTMLMessage>
          <FormattedHTMLMessage id="withdrawal.body.conditions">
            {(m) => <div dangerouslySetInnerHTML={{ __html: m }} />}
          </FormattedHTMLMessage>
          {!!client.hasPermanentWithdrawalForm && (
            <FormattedHTMLMessage id="withdrawal.body.conditionsPerm">
              {(m) => <div dangerouslySetInnerHTML={{ __html: m }} />}
            </FormattedHTMLMessage>
          )}
          <FormattedHTMLMessage id="withdrawal.body.procedure">
            {(m) => <div dangerouslySetInnerHTML={{ __html: m }} />}
          </FormattedHTMLMessage>
          <h3>
            <FormattedMessage id="withdrawal.form.title" />
          </h3>
          {!!hasWithdrawalFormMessage && this.getWithdrawalFormMessage()}
          {(moment(startWithdrawalDate) <= now &&
            now <= moment(endWithdrawalDate) && (
              <WithdrawalForm
                billingPeriod={billingPeriod}
                hasWithdrawalPopupMessage={hasWithdrawalPopupMessage}
                withdrawalPopupMessage={
                  localizations && localizations.withdrawalPopupMessage
                }
                withdrawalPopupTitle={
                  localizations && localizations.withdrawalPopupTitle
                }
                client={client}
                coverageGroups={coverageGroups}
                languageCode={languageCode}
                onSubmit={this.withdraw}
              />
            )) || (
            <MessageBox className="alert-warning bg-transparent">
              <FormattedMessage id="withdrawal.body.withdrawalClosed" />
            </MessageBox>
          )}
        </div>
      </PublicPage>
    );
  }
}

WithdrawalPage.propTypes = {
  billingPeriod: PropTypes.object,
  billingPeriodKey: PropTypes.string,
  client: PropTypes.object,
  clientDesignator: PropTypes.string,
  coverageGroups: PropTypes.array,
  intlActions: PropTypes.object,
  languageCode: PropTypes.string,
  loaded: PropTypes.bool,
  localizations: PropTypes.object,
  memberGuarantees: PropTypes.array,
  pageExists: PropTypes.bool,
  withdrawalActions: PropTypes.object,
  hasWithdrawalFormMessage: PropTypes.bool,
  hasWithdrawalPopupMessage: PropTypes.bool,
  hasWithdrawalRedirectButton: PropTypes.bool,
};

function mapStateToProps(state, ownProps) {
  const languageCode = ownProps.params.languageCode;
  const liveCheck = state.withdrawal.liveCheck || {};
  const page = state.withdrawal.page || {};
  const billingPeriod = page.billingPeriod || {};
  const client = page.client || {};
  const coverageGroups = page.withdrawals || [];

  const {
    hasWithdrawalForm,
    hasPermanentWithdrawalForm,
    hasAnnualWithdrawalForm,
    hasWithdrawalFormMessage,
    hasWithdrawalPopupMessage,
    hasWithdrawalRedirectButton,
    localizations,
    hasSessionWithdrawalForm,
  } = client;

  return {
    billingPeriod,
    billingPeriodKey: ownProps.params.billingPeriodKey,
    client,
    clientDesignator: ownProps.params.clientDesignator,
    coverageGroups,
    languageCode,
    localizations: (localizations && localizations[languageCode]) || {},
    loaded: !!page.loaded,
    memberGuarantees: liveCheck.guaranteeIds || [],
    pageExists:
      !!hasWithdrawalForm &&
      (!!hasPermanentWithdrawalForm ||
        !!hasAnnualWithdrawalForm ||
        !!hasSessionWithdrawalForm),
    hasWithdrawalFormMessage,
    hasWithdrawalPopupMessage,
    hasWithdrawalRedirectButton,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    intlActions: bindActionCreators(intlActions, dispatch),
    withdrawalActions: bindActionCreators(withdrawalActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(WithdrawalPage);



// WEBPACK FOOTER //
// ./src/components/withdrawal/WithdrawalPage.js